import { useState } from 'react';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Button } from '../../design';
import { useToast } from '../../design/Toast';
import { downloadCsv } from '../../lib/download';

export type ExportKind = components['schemas']['ExportKind'];
type Permission = components['schemas']['Permission'];

/** Permisiunea modulului cerută pe lângă `reports` (spec §4.4). */
export const EXPORT_MODULE: Record<ExportKind, Permission> = {
  operations: 'operations',
  'access-grants': 'keys',
  alerts: 'alerts',
  audit: 'audit',
  lockers: 'fleet',
};

export const EXPORT_LABEL: Record<ExportKind, string> = {
  operations: 'operațiuni',
  'access-grants': 'chei',
  alerts: 'alerte',
  audit: 'audit',
  lockers: 'lockere',
};

/**
 * „Export CSV” cu filtrele curente ale listei. Vizibil doar cu `reports` și permisiunea modulului;
 * descărcarea trece prin `fetch` cu Authorization, iar limita de 50 000 de rânduri e anunțată prin toast.
 */
export function ExportButton({
  kind,
  params,
  children,
}: {
  kind: ExportKind;
  params: Record<string, string | undefined>;
  children?: string;
}) {
  const { has } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  if (!has('reports') || !has(EXPORT_MODULE[kind])) return null;
  const run = async () => {
    setBusy(true);
    try {
      const r = await downloadCsv(`/exports/${kind}.csv`, params);
      toast(
        r.truncated
          ? `Export limitat la 50 000 de rânduri (${r.filename}). Restrânge filtrele pentru restul.`
          : `Fișierul ${r.filename} a fost descărcat.`,
        r.truncated ? 'info' : 'success',
      );
    } catch (e) {
      toast(errorMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      icon="report"
      busy={busy}
      onClick={() => void run()}
      aria-label={`Export CSV ${EXPORT_LABEL[kind]}`}
      title={`Export CSV ${EXPORT_LABEL[kind]} cu filtrele curente`}
    >
      {children ?? 'Export CSV'}
    </Button>
  );
}
